import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import service from '../services/userGuideService';
import { recordError } from '../utils/logService';

import {
  Messages,
  Notify,
  openNotification,
  util,
} from '@seims/shared';

// ---------------- Query Keys ----------------
const videoGuideKeys = {
  all: ['video-guides'],
};

const documentGuideKeys = {
  all: ['document-guides'],
};

// Constants

const videoGuideLabel = 'Video Guide';
const addEditGuideLabel = (isEdit, label) => `${isEdit ? "Update" : "Create"} ${label}`;
const addEditVideoGuideLabel = (isEdit) => addEditGuideLabel(isEdit, videoGuideLabel);
const docGuideLabel = 'Document Guide';
const addEditDocGuideLabel = (isEdit) => addEditGuideLabel(isEdit, videoGuideLabel);

// ---------------- Video Guides ----------------

/**
 * List video guides (optionally with params for future extensibility).
 * Returns: Array of video guides from API.
 */
export function useVideoGuides(params=[]) {
  return useQuery({
    queryKey: videoGuideKeys.all,
    queryFn: async () => {
      try {
        const res = await service.getVideoGuides();
        const {data = []} = res;
        return data
      } catch (error) {
        recordError(error);
        throw error;
      }
    },
    keepPreviousData: true,
    staleTime: 30_000,
  });
}

/**
 * Add/Edit (save) a video guide.
 * Uses POST when uuid is missing, PUT when uuid is present (handled by service).
 */
export function useSaveVideoGuide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: service.saveVideoGuide,
    onSuccess: (_data, vars) => {
      const isEdit = !!vars?.uuid;
      openNotification(
        addEditVideoGuideLabel(isEdit),
        util.parseMessage(isEdit ? Messages.UPDATE_SUCCESS : Messages.ADD_SUCCESS, videoGuideLabel),
        Notify.SUCCESS,
      );
      qc.invalidateQueries({ queryKey: videoGuideKeys.all });
    },
    onError: (error, vars) => {
        console.log("ON ERROR - ", error);
        
      const isEdit = !!vars?.uuid;
      recordError(error);
      openNotification(
        addEditVideoGuideLabel(isEdit),
        util.parseMessage(isEdit ? Messages.UPDATE_FAILURE : Messages.ADD_FAILURE, videoGuideLabel),
        Notify.ERROR,
      );
    },
  });
}

/**
 * Toggle archive state for video guide (PATCH).
 * Performs optimistic update with rollback.
 */
export function useToggleVideoGuideArchive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: service.patchVideoGuideArchive,
    onSuccess: (_data, vars) => {
      openNotification(
        videoGuideLabel,
        util.parseMessageWithNItems(Messages.GENERIC_ACTION_SUCCESS, [videoGuideLabel, vars?.archived ? 'archived' : 'un-archived']),
        Notify.SUCCESS,
      );
      qc.invalidateQueries({ queryKey: videoGuideKeys.all });
    },
    onError: (error, _vars) => {
      recordError(error);
      openNotification(videoGuideLabel, util.parseMessage(Messages.UPDATE_FAILURE, 'Archive state'), Notify.ERROR);
    },
  });
}

// ---------------- Document Guides ----------------

/**
 * List document/user guides.
 * Returns: Array of documents from API.
 */
export function useDocumentGuides() {
  return useQuery({
    queryKey: documentGuideKeys.all,
    queryFn: async () => {
      try {
        const res = await service.getUserGuides();
        const {data = [] } = res;
        return data
      } catch (error) {
        recordError(error);
        throw error;
      }
    },
    keepPreviousData: true,
    staleTime: 30_000,
  });
}

/**
 * Toggle archive state for document guide (PATCH).
 * Performs optimistic update with rollback.
 */
export function useToggleDocumentArchive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: service.patchUserGuideArchive,
    onSuccess: (_data, vars) => {
      openNotification(
        docGuideLabel,
        util.parseMessageWithNItems(Messages.GENERIC_ACTION_SUCCESS, [docGuideLabel, vars?.archive ? 'Archived' : 'Un-archived']),
        Notify.SUCCESS,
      );

      qc.invalidateQueries({ queryKey: documentGuideKeys.all });
    },
    onError: (error, _vars, ctx) => {
      recordError(error);
      openNotification(docGuideLabel, util.parseMessage(Messages.UPDATE_FAILURE, 'Archive state'), Notify.ERROR);
    }
  });
}

/**
 * Add/Edit (save) a document guide.
 * Uses POST when uuid is missing, PUT when uuid is present (handled by service).
 */
export function useSaveDocumentGuide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: service.saveDocument,
    onSuccess: (_data, vars) => {
      const isEdit = !!vars?.uuid;
      openNotification(
        addEditDocGuideLabel(isEdit),
        util.parseMessage(isEdit ? Messages.UPDATE_SUCCESS : Messages.ADD_SUCCESS, docGuideLabel),
        Notify.SUCCESS,
      );

      qc.invalidateQueries({ queryKey: documentGuideKeys.all });
    
    },
    onError: (error, vars) => {
        console.log("ON ERROR - ", error);
        
      const isEdit = !!vars?.uuid;
      recordError(error);
      openNotification(
        addEditDocGuideLabel(isEdit),
        util.parseMessage(isEdit ? Messages.UPDATE_FAILURE : Messages.ADD_FAILURE, docGuideLabel),
        Notify.ERROR,
      );
    },
  });
}

